import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useReactToPrint, type UseReactToPrintOptions } from 'react-to-print'
import { lmsPrintPdfApi, saveLmsPrintPdf } from '@/api/lmsPrintPdf'
import { PrintSnapshotError, serializePrintDocument } from '@/components/admin/print/serializePrintDocument'
import { getApiErrorMessage } from '@/utils/apiError'
import { getTenantId } from '@/utils/tenant'

export function useLmsPrintPdfCapability() {
  return useQuery({
    queryKey: ['lc', 'print-pdf', 'capability', getTenantId()],
    queryFn: lmsPrintPdfApi.capability,
    retry: false,
    staleTime: 30_000,
  })
}

export interface UseLmsPrintOptions extends UseReactToPrintOptions {
  /** Data, selected mode, image pagination, and the output ref must be ready. */
  canPrint?: boolean
  /** Disable for an embedded output that shares a route with another print hook. */
  autoDownload?: boolean
}

async function waitForPrintFonts(document: Document) {
  if (!document.fonts) return
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      document.fonts.ready,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new PrintSnapshotError('PRINT_PDF_ASSET_FAILED')), 15_000)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Browser print and PDF share react-to-print's prepared iframe and exact page styles. */
export function useLmsPrint({ canPrint = true, autoDownload = true, ...options }: UseLmsPrintOptions) {
  const { t, i18n } = useTranslation('common')
  const capability = useLmsPrintPdfCapability()
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inProgress = useRef(false)
  const autoStarted = useRef(false)
  const canDownloadPdf = capability.isSuccess && capability.data?.enabled === true && !!capability.data.assetBaseUrl

  const finish = useCallback(() => {
    inProgress.current = false
    setIsDownloading(false)
  }, [])

  const reportError = useCallback((cause: unknown) => {
    const message = cause instanceof PrintSnapshotError
      ? t(`apiError.${cause.code}`, { defaultValue: t('printPdf.error') })
      : getApiErrorMessage(cause, t)
    setError(message)
    finish()
  }, [finish, t])

  // Keep the ordinary print hook last so its existing lifecycle remains unchanged.
  const preparePdf = useReactToPrint({
    ...options,
    onAfterPrint: finish,
    onPrintError: (_location, cause) => reportError(cause),
    print: async (iframe) => {
      try {
        const document = iframe.contentDocument
        const assetBaseUrl = capability.data?.assetBaseUrl
        if (!document || !assetBaseUrl || !canDownloadPdf) throw new PrintSnapshotError('PRINT_PDF_INVALID_HTML')
        await waitForPrintFonts(document)
        const failedImage = Array.from(document.images).some((image) => image.getAttribute('src') && (!image.complete || image.naturalWidth === 0))
        if (failedImage) throw new PrintSnapshotError('PRINT_PDF_ASSET_FAILED')
        const html = serializePrintDocument(document, assetBaseUrl)
        const title = typeof options.documentTitle === 'function' ? options.documentTitle() : options.documentTitle
        const fileName = title || 'print'
        const response = await lmsPrintPdfApi.download({
          html,
          fileName,
          language: (i18n.resolvedLanguage ?? i18n.language).startsWith('en') ? 'en' : 'ko',
        })
        saveLmsPrintPdf(response.data, response.headers['content-disposition'], fileName)
      } catch (cause) {
        reportError(cause)
      } finally {
        finish()
      }
    },
  })
  const print = useReactToPrint(options)

  const downloadPdf = useCallback(() => {
    if (!canDownloadPdf || !canPrint || !options.contentRef?.current || inProgress.current) return
    inProgress.current = true
    setError(null)
    setIsDownloading(true)
    try { preparePdf() } catch (cause) { reportError(cause) }
  }, [canDownloadPdf, canPrint, options.contentRef, preparePdf, reportError])

  useEffect(() => {
    if (!autoDownload || autoStarted.current || !canDownloadPdf || !canPrint || !options.contentRef?.current) return
    if (new URLSearchParams(window.location.search).get('downloadPdf') !== '1') return
    autoStarted.current = true
    downloadPdf()
  }, [autoDownload, canDownloadPdf, canPrint, downloadPdf, options.contentRef])

  return { print, downloadPdf, canDownloadPdf, isDownloading, error }
}
